/**
 * Agrupamento da lista de Meetings por período (card 3162, design `Meetings.dc.html`): Hoje, Amanhã,
 * Esta semana, Ontem e, fora disso, o mês ("Setembro"; de outro ano, "Setembro de 2025").
 *
 * Funções puras: quem chama passa o "hoje" (do `DateFormatterService`) e as datas já convertidas. Os
 * grupos seguem a ordem que o servidor mandou: itens consecutivos do mesmo período ficam juntos, e
 * a ordem de cada aba (Próximas crescente, Recentes decrescente, Todas com as que aconteceram antes
 * das agendadas, card 3191) não muda.
 */

export interface GrupoDePeriodo<T> {
  /** Estável entre cargas, para o `track` do template. */
  chave: string;
  rotulo: string;
  itens: T[];
}

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const UM_DIA_MS = 24 * 60 * 60 * 1000;

/** Meia-noite local do dia, para comparar dias civis e não instantes. */
function inicioDoDia(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate());
}

/** Diferença em dias civis (`data` − `hoje`), sem se perder no horário de verão. */
function diasEntre(hoje: Date, data: Date): number {
  return Math.round((inicioDoDia(data).getTime() - inicioDoDia(hoje).getTime()) / UM_DIA_MS);
}

/** Dias que faltam até o domingo desta semana (semana de segunda a domingo). */
function diasAteODomingo(hoje: Date): number {
  const diaDaSemana = hoje.getDay(); // 0 = domingo
  return diaDaSemana === 0 ? 0 : 7 - diaDaSemana;
}

/** Rótulo do período de uma data em relação a hoje. */
export function rotuloDoPeriodo(data: Date, hoje: Date): { chave: string; rotulo: string } {
  const dias = diasEntre(hoje, data);

  if (dias === 0) return { chave: 'hoje', rotulo: 'Hoje' };
  if (dias === 1) return { chave: 'amanha', rotulo: 'Amanhã' };
  if (dias === -1) return { chave: 'ontem', rotulo: 'Ontem' };
  if (dias > 1 && dias <= diasAteODomingo(hoje)) return { chave: 'esta-semana', rotulo: 'Esta semana' };

  const ano = data.getFullYear();
  const mes = MESES[data.getMonth()];
  return {
    chave: `mes-${ano}-${data.getMonth()}`,
    rotulo: ano === hoje.getFullYear() ? mes : `${mes} de ${ano}`,
  };
}

/**
 * Agrupa na ordem recebida. Item sem data válida vai para o grupo "Sem data", no lugar em que
 * aparecer.
 */
export function agruparPorPeriodo<T>(
  itens: readonly T[],
  dataDe: (item: T) => Date | null | undefined,
  hoje: Date
): GrupoDePeriodo<T>[] {
  const grupos: GrupoDePeriodo<T>[] = [];
  const vistos = new Map<string, number>();
  let periodoDoUltimo: string | null = null;

  for (const item of itens) {
    const data = dataDe(item);
    const periodo =
      data && !Number.isNaN(data.getTime())
        ? rotuloDoPeriodo(data, hoje)
        : { chave: 'sem-data', rotulo: 'Sem data' };

    if (periodo.chave === periodoDoUltimo) {
      grupos[grupos.length - 1].itens.push(item);
      continue;
    }
    periodoDoUltimo = periodo.chave;

    // O mesmo período pode voltar depois de outro (ex.: Todas, com futuras e passadas): a chave
    // ganha um sufixo para o `track` continuar único.
    const vezes = vistos.get(periodo.chave) ?? 0;
    vistos.set(periodo.chave, vezes + 1);
    grupos.push({
      chave: vezes ? `${periodo.chave}#${vezes}` : periodo.chave,
      rotulo: periodo.rotulo,
      itens: [item],
    });
  }

  return grupos;
}
